import { useRef, useState } from "react";
import { X, UploadCloud, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Demo-only "Request Credential Update" modal.
 * Explains that workers cannot edit verified credentials and lets them
 * submit an updated credential for re-verification. The upload is a
 * placeholder — no file is transmitted.
 */
export default function RequestCredentialUpdateModal({
  credentialName,
  onClose,
}) {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    setSubmitting(true);
    // Demo-only — simulate a brief upload then close.
    setTimeout(() => {
      toast.success("Updated credential submitted for verification", {
        description: "ProCred will review and replace the previous record once verified.",
      });
      setSubmitting(false);
      onClose?.();
    }, 700);
  };

  return (
    <div
      data-testid="request-update-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1221]/70 p-6"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md border border-slate-200 bg-white p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="request-update-close"
          onClick={onClose}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center text-slate-400 hover:text-[#0B1221]"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
          Issuer-owned record
        </div>
        <h3 className="font-display mt-1 text-2xl font-bold tracking-tight text-[#0B1221]">
          Request Credential Update
        </h3>
        {credentialName && (
          <div className="mt-1 text-xs text-slate-500">For: <span className="font-semibold text-[#0B1221]">{credentialName}</span></div>
        )}

        <p className="mt-4 text-sm leading-relaxed text-slate-700">
          Verified credentials cannot be edited by workers. If this credential has been
          renewed or contains incorrect information, submit the updated credential for
          verification. Once verified, ProCred will replace the previous credential.
        </p>

        <label
          data-testid="request-update-dropzone"
          className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center hover:border-[#0B1221]"
        >
          {file ? (
            <>
              <FileCheck2 className="h-7 w-7 text-emerald-600" strokeWidth={1.75} />
              <div className="text-sm font-semibold text-[#0B1221]">{file.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Ready to submit</div>
            </>
          ) : (
            <>
              <UploadCloud className="h-7 w-7 text-slate-400" strokeWidth={1.75} />
              <div className="text-sm font-semibold text-[#0B1221]">Select updated credential</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">PDF · JPG · PNG · MVP build</div>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            data-testid="request-update-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            data-testid="request-update-cancel"
            onClick={onClose}
            className="flex-1 border border-slate-300 bg-white px-4 py-3 text-sm font-semibold uppercase tracking-wider text-[#0B1221] transition-colors hover:border-[#0B1221]"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="request-update-submit"
            onClick={submit}
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF5A1F] px-4 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#e94d14] disabled:opacity-60"
          >
            <UploadCloud className="h-4 w-4" strokeWidth={2.5} />
            {submitting ? "Submitting…" : "Upload Updated Credential"}
          </button>
        </div>
      </div>
    </div>
  );
}
